/** Lays out the conversation column and its docking artifact panel; owns the page-local panel state. */

import { useEffect, useRef, useState } from "react"
import type { ReactNode } from "react"
import { Box } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { useAppDispatch, useAppSelector, useAppStore } from "hooks/redux"
import { collapsePanel, expandPanel, selectPanelCollapsed } from "../store/agent-chat-slice"
import { ArtifactPanel } from "./artifact-panel"
import { ArtifactPanelProvider, useArtifactPanel } from "./use-artifact-panel"
import { DEFAULT_PANEL_WIDTH_PX, clampPanelWidth } from "./use-panel-resize"

const AREA_SX = { position: "relative", display: "flex", flex: 1, minHeight: 0 } satisfies SxProps<Theme>
// tabIndex -1: stable focus fallback when the opening card is gone on close. position:relative: containing block for the composer + disclaimer overlay.
const CHAT_COLUMN_SX = { position: "relative", display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0, outline: "none" } satisfies SxProps<Theme>

export function ArtifactPanelRegion({ activeThreadId, children }: { activeThreadId: string | null, children: ReactNode }) {
  const chatColumnRef = useRef<HTMLDivElement>(null)
  const { artifact, isExpanded, close, toggleExpand, handleEscape, contextValue } = useArtifactPanel(activeThreadId, chatColumnRef)
  const [panelWidth, setPanelWidth] = useState(() => clampPanelWidth(DEFAULT_PANEL_WIDTH_PX, window.innerWidth))
  const rafRef = useRef<number | null>(null)

  // Re-clamp against a shrinking viewport so the docked panel can't exceed 75vw after a window resize.
  // rAF-gated so a resize storm coalesces into one re-clamp per frame rather than one per event.
  useEffect(() => {
    const onViewportResize = () => {
      if (rafRef.current != null) return
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null
        setPanelWidth((current) => clampPanelWidth(current, window.innerWidth))
      })
    }
    window.addEventListener("resize", onViewportResize)
    return () => {
      window.removeEventListener("resize", onViewportResize)
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current)
    }
  }, [])

  // An open artifact — or entering full-screen — needs the horizontal room, so it auto-collapses the
  // conversations panel and restores it on close (but only what WE auto-collapsed; a user's own prior collapse
  // is left alone). The two transitions are tracked independently so a re-render that changes neither is a
  // no-op; every close cause (button, Esc, thread switch) restores. Dispatch-in-effect, not setState; the
  // collapse flag is read FRESH off the precisely-typed agentChat slice, never a render closure.
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const panelCollapsed = useAppSelector(selectPanelCollapsed)
  const hasArtifact = artifact != null
  const prevHasArtifactRef = useRef(hasArtifact)
  const prevIsExpandedRef = useRef(isExpanded)
  const autoCollapsedRef = useRef(false)
  // Any re-expand while our latch is set means someone other than our close path re-docked the panel (the user
  // clicked the rail's Expand). Relinquish ownership so a subsequent MANUAL collapse isn't force-expanded when the
  // artifact closes — we only ever restore a collapse that is still ours. (Our own close-time expand clears the
  // latch first, so this is a no-op there; our auto-collapse sets panelCollapsed=true, so it never trips this.)
  useEffect(() => {
    if (!panelCollapsed) autoCollapsedRef.current = false
  }, [panelCollapsed])
  useEffect(() => {
    // Open ↔ close: open auto-collapses; close restores only what we auto-collapsed.
    if (prevHasArtifactRef.current !== hasArtifact) {
      prevHasArtifactRef.current = hasArtifact
      if (hasArtifact) {
        if (!store.getState().agentChat.panelCollapsed) {
          dispatch(collapsePanel())
          autoCollapsedRef.current = true
        }
      } else {
        if (autoCollapsedRef.current) dispatch(expandPanel())
        autoCollapsedRef.current = false
      }
    }

    // Entering full-screen while docked also collapses the drawer (matching open); exiting does not restore it.
    if (prevIsExpandedRef.current !== isExpanded) {
      prevIsExpandedRef.current = isExpanded
      if (isExpanded && hasArtifact && !store.getState().agentChat.panelCollapsed) {
        dispatch(collapsePanel())
        autoCollapsedRef.current = true
      }
    }
  }, [hasArtifact, isExpanded, dispatch, store])

  return (
    <ArtifactPanelProvider value={contextValue}>
      <Box sx={AREA_SX}>
        <Box ref={chatColumnRef} tabIndex={-1} sx={CHAT_COLUMN_SX}>{children}</Box>
        {artifact != null && (
          <ArtifactPanel
            key={artifact.id}
            artifact={artifact}
            isExpanded={isExpanded}
            onClose={close}
            onToggleExpand={toggleExpand}
            onEscape={handleEscape}
            fallbackFocusRef={chatColumnRef}
            width={panelWidth}
            onWidthChange={setPanelWidth}
            threadId={activeThreadId}
          />
        )}
      </Box>
    </ArtifactPanelProvider>
  )
}
